import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, Card, TextField, Button, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Chip, IconButton, Dialog, DialogTitle,
  DialogContent, DialogActions, Grid, MenuItem, InputAdornment, CircularProgress,
  Avatar, Pagination, Tooltip, Alert, Select, FormControl, InputLabel
} from '@mui/material';
import { PersonAdd, Search, Visibility, Close, FilterList } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import api from '../../api/axios';
import toast from 'react-hot-toast';

const bloodGroups = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-', 'Unknown'];
const genders = ['male', 'female', 'other'];
const bloodColors: Record<string, string> = {
  'A+': '#ef4444', 'A-': '#dc2626', 'B+': '#3b82f6', 'B-': '#2563eb',
  'AB+': '#8b5cf6', 'AB-': '#7c3aed', 'O+': '#10b981', 'O-': '#059669', 'Unknown': '#94a3b8'
};

const defaultForm = {
  name: '', phone: '', email: '', gender: 'male', bloodGroup: 'Unknown',
  dob: '', occupation: '', maritalStatus: '',
  'address.street': '', 'address.city': '', 'address.state': '', 'address.pincode': '',
  'emergencyContacts[0].name': '', 'emergencyContacts[0].phone': '', 'emergencyContacts[0].relation': '',
  'vitals.height': '', 'vitals.weight': '', 'vitals.bloodPressure': '', 'vitals.pulse': ''
};

const Label = ({ children }: any) => (
  <Typography variant="caption" sx={{ fontWeight: 600, color: '#64748b', mb: 0.5, display: 'block' }}>{children}</Typography>
);

const SectionTitle = ({ children }: any) => (
  <Box sx={{ gridColumn: '1 / -1', mt: 1.5 }}>
    <Typography variant="subtitle2" sx={{ color: '#1e40af', fontWeight: 700, pb: 0.5, borderBottom: '2px solid #dbeafe', mb: 1 }}>{children}</Typography>
  </Box>
);

const Patients = () => {
  const navigate = useNavigate();
  const [patients, setPatients] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterBlood, setFilterBlood] = useState('');
  const [filterGender, setFilterGender] = useState('');
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<any>(defaultForm);
  const [submitting, setSubmitting] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get('/patients', {
        params: { search, limit: 15, page, bloodGroup: filterBlood || undefined, gender: filterGender || undefined }
      });
      setPatients(res.data.data || []);
      setTotal(res.data.total || 0);
    } catch { toast.error('Failed to load patients'); }
    finally { setLoading(false); }
  }, [search, page, filterBlood, filterGender]);

  useEffect(() => { load(); }, [load]);

  const handleSubmit = async () => {
    if (!form.name) { setError('Full name is required'); return; }
    setError(''); setSubmitting(true);
    try {
      const emergencyContact = form['emergencyContacts[0].name'] ? [{
        name: form['emergencyContacts[0].name'],
        phone: form['emergencyContacts[0].phone'],
        relation: form['emergencyContacts[0].relation'],
        isPrimary: true
      }] : [];

      const vitals: any = {};
      if (form['vitals.height']) vitals.height = Number(form['vitals.height']);
      if (form['vitals.weight']) vitals.weight = Number(form['vitals.weight']);
      if (form['vitals.bloodPressure']) vitals.bloodPressure = form['vitals.bloodPressure'];
      if (form['vitals.pulse']) vitals.pulse = Number(form['vitals.pulse']);

      await api.post('/patients', {
        name: form.name, phone: form.phone, email: form.email,
        gender: form.gender, bloodGroup: form.bloodGroup,
        dob: form.dob || undefined, occupation: form.occupation,
        maritalStatus: form.maritalStatus || undefined,
        address: {
          street: form['address.street'], city: form['address.city'],
          state: form['address.state'], pincode: form['address.pincode']
        },
        emergencyContacts: emergencyContact,
        vitals: Object.keys(vitals).length ? vitals : undefined
      });
      toast.success('Patient registered successfully');
      setOpen(false);
      setForm(defaultForm);
      load();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to register patient');
    } finally { setSubmitting(false); }
  };

  const set = (k: string, v: string) => setForm((f: any) => ({ ...f, [k]: v }));
  const getAge = (dob: string) => dob ? `${new Date().getFullYear() - new Date(dob).getFullYear()} yrs` : '—';

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 800, color: '#0f172a' }}>Patients</Typography>
          <Typography variant="body2" sx={{ color: '#64748b', mt: 0.3 }}>{total} registered patients</Typography>
        </Box>
        <Button variant="contained" startIcon={<PersonAdd />} onClick={() => { setOpen(true); setError(''); setForm(defaultForm); }}
          sx={{ borderRadius: 2, bgcolor: '#1e40af', '&:hover': { bgcolor: '#1d4ed8' }, textTransform: 'none', fontWeight: 600, px: 2.5 }}>
          Register Patient
        </Button>
      </Box>

      {/* Search & Filters */}
      <Card elevation={0} sx={{ border: '1px solid #e2e8f0', borderRadius: 3, p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField
            placeholder="Search by name, patient ID, phone or email..."
            value={search} onChange={e => { setSearch(e.target.value); setPage(1); }}
            size="small" sx={{ flex: 1, minWidth: 280, '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#f8fafc' } }}
            InputProps={{ startAdornment: <InputAdornment position="start"><Search sx={{ color: '#94a3b8', fontSize: 20 }} /></InputAdornment> }}
          />
          <FormControl size="small" sx={{ minWidth: 130 }}>
            <InputLabel>Blood Group</InputLabel>
            <Select label="Blood Group" value={filterBlood} onChange={e => { setFilterBlood(e.target.value); setPage(1); }}
              sx={{ borderRadius: 2 }}>
              <MenuItem value="">All</MenuItem>
              {bloodGroups.map(b => <MenuItem key={b} value={b}>{b}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Gender</InputLabel>
            <Select label="Gender" value={filterGender} onChange={e => { setFilterGender(e.target.value); setPage(1); }}
              sx={{ borderRadius: 2 }}>
              <MenuItem value="">All</MenuItem>
              {genders.map(g => <MenuItem key={g} value={g} sx={{ textTransform: 'capitalize' }}>{g.charAt(0).toUpperCase() + g.slice(1)}</MenuItem>)}
            </Select>
          </FormControl>
          {(filterBlood || filterGender) && (
            <Button size="small" onClick={() => { setFilterBlood(''); setFilterGender(''); }} sx={{ textTransform: 'none', color: '#64748b' }}>
              Clear filters
            </Button>
          )}
        </Box>
      </Card>

      {/* Table */}
      <Card elevation={0} sx={{ border: '1px solid #e2e8f0', borderRadius: 3, overflow: 'hidden' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress sx={{ color: '#3b82f6' }} /></Box>
        ) : (
          <>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f8fafc' }}>
                    {['Patient', 'Patient ID', 'Age / Gender', 'Blood Group', 'Phone', 'Location', 'Actions'].map(h => (
                      <TableCell key={h} sx={{ fontWeight: 700, color: '#374151', py: 1.5, fontSize: 13, borderBottom: '2px solid #e2e8f0' }}>{h}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {patients.map(p => (
                    <TableRow key={p._id} hover sx={{ cursor: 'pointer', '&:hover': { bgcolor: '#f8fafc' } }}
                      onClick={() => navigate(`/patients/${p._id}`)}>
                      <TableCell onClick={e => e.stopPropagation()}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Avatar sx={{ width: 38, height: 38, bgcolor: '#dbeafe', color: '#1d4ed8', fontSize: 15, fontWeight: 700 }}>
                            {p.name?.charAt(0).toUpperCase()}
                          </Avatar>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 600, color: '#0f172a' }}>{p.name}</Typography>
                            <Typography variant="caption" sx={{ color: '#94a3b8' }}>{p.email || '—'}</Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell onClick={e => e.stopPropagation()}>
                        <Chip label={p.patientId} size="small" sx={{ bgcolor: '#f1f5f9', color: '#475569', fontWeight: 600, fontSize: 11 }} />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                          {getAge(p.dob)} / {p.gender || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {p.bloodGroup && p.bloodGroup !== 'Unknown' ? (
                          <Chip label={p.bloodGroup} size="small"
                            sx={{ bgcolor: `${bloodColors[p.bloodGroup]}18`, color: bloodColors[p.bloodGroup], fontWeight: 700, fontSize: 11, border: `1px solid ${bloodColors[p.bloodGroup]}35` }} />
                        ) : <Typography variant="body2" sx={{ color: '#94a3b8' }}>—</Typography>}
                      </TableCell>
                      <TableCell><Typography variant="body2">{p.phone || '—'}</Typography></TableCell>
                      <TableCell><Typography variant="body2">{[p.address?.city, p.address?.state].filter(Boolean).join(', ') || '—'}</Typography></TableCell>
                      <TableCell onClick={e => e.stopPropagation()}>
                        <Tooltip title="View Full Profile">
                          <IconButton size="small" onClick={() => navigate(`/patients/${p._id}`)}
                            sx={{ color: '#3b82f6', '&:hover': { bgcolor: '#eff6ff' } }}>
                            <Visibility fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                  {patients.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 8 }}>
                        <Typography sx={{ color: '#94a3b8' }}>
                          {search ? `No patients found for "${search}"` : 'No patients registered yet'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            {total > 15 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2, borderTop: '1px solid #f1f5f9' }}>
                <Pagination count={Math.ceil(total / 15)} page={page} onChange={(_, v) => setPage(v)} color="primary" size="small" />
              </Box>
            )}
          </>
        )}
      </Card>

      {/* Register Patient Dialog */}
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontWeight: 700, color: '#0f172a', borderBottom: '1px solid #f1f5f9' }}>
          Register New Patient
          <IconButton size="small" onClick={() => setOpen(false)}><Close fontSize="small" /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{error}</Alert>}
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>

            <SectionTitle>Personal Information</SectionTitle>

            <Box>
              <Label>Full Name *</Label>
              <TextField fullWidth size="small" value={form.name} onChange={e => set('name', e.target.value)} placeholder="Enter full name" sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
            </Box>
            <Box>
              <Label>Phone Number</Label>
              <TextField fullWidth size="small" value={form.phone} onChange={e => set('phone', e.target.value)} placeholder="Mobile number" sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
            </Box>
            <Box>
              <Label>Email</Label>
              <TextField fullWidth size="small" type="email" value={form.email} onChange={e => set('email', e.target.value)} placeholder="Email address" sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
            </Box>
            <Box>
              <Label>Date of Birth</Label>
              <TextField fullWidth size="small" type="date" value={form.dob} onChange={e => set('dob', e.target.value)} InputLabelProps={{ shrink: true }} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
            </Box>
            <Box>
              <Label>Gender</Label>
              <TextField select fullWidth size="small" value={form.gender} onChange={e => set('gender', e.target.value)} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}>
                {genders.map(g => <MenuItem key={g} value={g} sx={{ textTransform: 'capitalize' }}>{g.charAt(0).toUpperCase() + g.slice(1)}</MenuItem>)}
              </TextField>
            </Box>
            <Box>
              <Label>Blood Group</Label>
              <TextField select fullWidth size="small" value={form.bloodGroup} onChange={e => set('bloodGroup', e.target.value)} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}>
                {bloodGroups.map(g => <MenuItem key={g} value={g}>{g}</MenuItem>)}
              </TextField>
            </Box>
            <Box>
              <Label>Marital Status</Label>
              <TextField select fullWidth size="small" value={form.maritalStatus} onChange={e => set('maritalStatus', e.target.value)} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}>
                {['single', 'married', 'divorced', 'widowed'].map(m => <MenuItem key={m} value={m} sx={{ textTransform: 'capitalize' }}>{m.charAt(0).toUpperCase() + m.slice(1)}</MenuItem>)}
              </TextField>
            </Box>
            <Box>
              <Label>Occupation</Label>
              <TextField fullWidth size="small" value={form.occupation} onChange={e => set('occupation', e.target.value)} placeholder="e.g. Engineer, Teacher" sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
            </Box>

            <SectionTitle>Address</SectionTitle>

            <Box sx={{ gridColumn: '1 / -1' }}>
              <Label>Street Address</Label>
              <TextField fullWidth size="small" value={form['address.street']} onChange={e => set('address.street', e.target.value)} placeholder="House no, Street, Area" sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
            </Box>
            <Box>
              <Label>City</Label>
              <TextField fullWidth size="small" value={form['address.city']} onChange={e => set('address.city', e.target.value)} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
            </Box>
            <Box>
              <Label>State</Label>
              <TextField fullWidth size="small" value={form['address.state']} onChange={e => set('address.state', e.target.value)} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
            </Box>
            <Box>
              <Label>Pincode</Label>
              <TextField fullWidth size="small" value={form['address.pincode']} onChange={e => set('address.pincode', e.target.value)} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
            </Box>

            <SectionTitle>Emergency Contact</SectionTitle>

            <Box>
              <Label>Contact Name</Label>
              <TextField fullWidth size="small" value={form['emergencyContacts[0].name']} onChange={e => set('emergencyContacts[0].name', e.target.value)} placeholder="Full name" sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
            </Box>
            <Box>
              <Label>Contact Phone</Label>
              <TextField fullWidth size="small" value={form['emergencyContacts[0].phone']} onChange={e => set('emergencyContacts[0].phone', e.target.value)} placeholder="Mobile number" sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
            </Box>
            <Box>
              <Label>Relation</Label>
              <TextField fullWidth size="small" value={form['emergencyContacts[0].relation']} onChange={e => set('emergencyContacts[0].relation', e.target.value)} placeholder="e.g. Spouse, Parent, Child" sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
            </Box>

            <SectionTitle>Initial Vitals (Optional)</SectionTitle>

            <Box>
              <Label>Height (cm)</Label>
              <TextField fullWidth size="small" type="number" value={form['vitals.height']} onChange={e => set('vitals.height', e.target.value)} placeholder="e.g. 170" sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
            </Box>
            <Box>
              <Label>Weight (kg)</Label>
              <TextField fullWidth size="small" type="number" value={form['vitals.weight']} onChange={e => set('vitals.weight', e.target.value)} placeholder="e.g. 65" sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
            </Box>
            <Box>
              <Label>Blood Pressure</Label>
              <TextField fullWidth size="small" value={form['vitals.bloodPressure']} onChange={e => set('vitals.bloodPressure', e.target.value)} placeholder="e.g. 120/80" sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
            </Box>
            <Box>
              <Label>Pulse (bpm)</Label>
              <TextField fullWidth size="small" type="number" value={form['vitals.pulse']} onChange={e => set('vitals.pulse', e.target.value)} placeholder="e.g. 72" sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid #f1f5f9' }}>
          <Button onClick={() => setOpen(false)} sx={{ textTransform: 'none', color: '#64748b' }}>Cancel</Button>
          <Button variant="contained" onClick={handleSubmit} disabled={submitting}
            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, bgcolor: '#1e40af', '&:hover': { bgcolor: '#1d4ed8' }, px: 3 }}>
            {submitting ? <CircularProgress size={18} sx={{ color: 'white' }} /> : 'Register Patient'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Patients;
